import {
    Card,
    CardContent,
    CardActions,
    Button,
    Typography,
    Divider,
    Grid,
    Link,
    Avatar,
    IconButton,
    Tooltip,
    Box,
    Chip,
    Stack,
} from "@mui/material";
import {
    WorkHistory,
    Cake,
    Timeline,
    Public,
    Language,
    TravelExplore,
    VolunteerActivism,
    EmojiEvents,
    Badge,
    Email,
    Description,
    VerifiedUser,
    ArrowForward,
    FileOpen,
    Person,
    AccountCircle,
    Stars,
} from '@mui/icons-material';
import IconTextRow from "../Shared/IconTextRow";
import CardSection from "../Shared/CardSection";
import { fetchDocumentUrlAndOpenDialog } from "../../services/sharepoint.service";
import { getImageSrc } from "../../utils/utilFunctions";
import { SingleExpertDataType } from "../../types/dataTypes";

type ExpertCardProps = {
    item: SingleExpertDataType;
    onDetailsClick: (expertID: number) => void;
    accessToken: string;
};

// Falls back to the expert's initials (e.g. "Jane Doe" -> "JD") when there's no photo yet,
// so cards stay visually consistent whether or not a real photo has been uploaded.
function getInitials(name?: string): string {
    if (!name) return '';
    const parts = name.trim().split(/\s+/).filter(Boolean);
    return parts.slice(0, 2).map((part) => part[0]?.toUpperCase() ?? '').join('');
}

// Splits a semicolon-separated field (e.g. "ADB; EU; UNIDO") into deduped, trimmed tags for chip
// display - some source data has accidental repeats (e.g. "East Europe" listed 3 times), which
// look odd as plain text but are easy to silently clean up as chips without touching the data.
function splitTags(value?: string): string[] {
    if (!value) return [];
    const parts = value.split(';').map((part) => part.trim()).filter(Boolean);
    return Array.from(new Set(parts));
}

function ExpertCard({ item, onDetailsClick, accessToken }: ExpertCardProps) {
    const imageSrc = getImageSrc(item.photo);
    const initials = getInitials(item.name);
    const hasConsent = item.consent === true;

    return (
        <Card sx={{ transition: 'box-shadow 0.2s ease', '&:hover': { boxShadow: 6 } }}>
            <Box sx={{ height: 6, backgroundColor: 'primary.main' }} />
            <CardContent>
                <Grid container spacing={2} alignItems="center" wrap="nowrap">
                    <Grid item>
                        <Avatar
                            src={imageSrc || undefined}
                            sx={{
                                width: 76,
                                height: 76,
                                fontSize: '1.5rem',
                                fontWeight: 600,
                                backgroundColor: imageSrc ? 'transparent' : 'primary.main',
                                border: '3px solid',
                                borderColor: 'primary.light',
                                '& img': {
                                    objectFit: 'cover',
                                    width: '100%',
                                    height: '100%',
                                },
                            }}
                        >
                            {!imageSrc && (initials || <Person />)}
                        </Avatar>
                    </Grid>
                    <Grid item xs>
                        <Link
                            component="button"
                            onClick={() => onDetailsClick(item.expertID)}
                            underline="none"
                        >
                            <Typography variant="h6" color={'primary'} style={{ textAlign: 'left', fontWeight: 700 }}>
                                {item.name}
                            </Typography>
                        </Link>
                        <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ marginTop: 0.5 }}>
                            <Chip
                                size="small"
                                icon={<VerifiedUser fontSize="small" />}
                                label={hasConsent ? 'Consent given' : 'No consent'}
                                color={hasConsent ? 'success' : 'default'}
                                variant={hasConsent ? 'filled' : 'outlined'}
                            />
                            {item.cvYear && (
                                <Chip size="small" icon={<Description fontSize="small" />} label={`CV ${item.cvYear}`} variant="outlined" />
                            )}
                            {!!item.referencesWorkedCount && (
                                <Chip size="small" icon={<Stars fontSize="small" />}
                                    label={`${item.referencesWorkedCount} projects${item.referencesViaPartnerCount ? ` · ${item.referencesViaPartnerCount} via partner` : ''}`}
                                    color="primary" variant="outlined" />
                            )}
                            {item.viaPartners && (
                                <Tooltip title={`via ${item.viaPartners}`}>
                                    <Chip size="small" variant="outlined" label={`via ${item.viaPartners}`} sx={{ maxWidth: 260 }} />
                                </Tooltip>
                            )}
                        </Stack>
                    </Grid>
                </Grid>

                <CardSection label="Profile" IconComponent={AccountCircle}>
                    <Grid item xs={1}>
                        <IconTextRow title="Age" IconComponent={Cake} displayText={item.Age ?? '-'} />
                    </Grid>
                    <Grid item xs={2}>
                        <IconTextRow title="Years of Experience" IconComponent={Timeline} displayText={item.years_of_work_experience ?? '-'} />
                    </Grid>
                    <Grid item xs={2}>
                        <IconTextRow title="Nationality" IconComponent={Public} displayText={item.nationality || '-'} country={item.nationality} />
                    </Grid>
                    <Grid item xs={2}>
                        <IconTextRow title="Languages" IconComponent={Language} displayText={splitTags(item.languages).join(', ') || '-'} />
                    </Grid>
                    <Grid item xs={4}>
                        <IconTextRow title="Email" IconComponent={Email} displayText={item.email || '-'} />
                    </Grid>
                </CardSection>

                <CardSection label="Expertise" IconComponent={WorkHistory}>
                    <Grid item xs={12}>
                        <IconTextRow title="Experience / Keywords" IconComponent={WorkHistory} chips={splitTags(item.keywords)} />
                    </Grid>
                    <Grid item xs={6}>
                        <IconTextRow title="Regions" IconComponent={TravelExplore} chips={splitTags(item.regions)} />
                    </Grid>
                    <Grid item xs={6}>
                        <IconTextRow title="Donors" IconComponent={VolunteerActivism} chips={splitTags(item.donors)} />
                    </Grid>
                      <Grid item xs={12}>
                        <IconTextRow title="Recent Project Positions" IconComponent={Badge} chips={splitTags(item.positions)} />
                    </Grid>
                </CardSection>
            </CardContent>
            <Divider style={{ color: 'lightgrey' }} />
            <CardActions>
                <Grid container justifyContent="space-between" alignItems="center">
                    <Grid item>
                        <Tooltip title="View Latest CV">
                            <span>
                                <IconButton
                                    onClick={() => fetchDocumentUrlAndOpenDialog(item.cvlink, accessToken)}
                                    disabled={!item.cvlink}
                                    color="primary"
                                >
                                    <FileOpen />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Grid>
                    <Grid item>
                        <Button
                            startIcon={<ArrowForward />}
                            size="small"
                            variant="text"
                            color="primary"
                            onClick={() => onDetailsClick(item.expertID)}
                            sx={{ whiteSpace: 'nowrap' }}
                        >
                            View Details
                        </Button>
                    </Grid>
                </Grid>
            </CardActions>
        </Card>
    );
}

export default ExpertCard;
